/**
 * PostHog — the browser half.
 *
 * `instrumentation-client.ts` runs before hydration on EVERY route, so the
 * decision of whether PostHog may start at all lives here, behind the same
 * predicate Google Analytics, Dub, New Relic and the browser Sentry SDK use
 * (`lib/analytics/first-party-tracking-scope.ts`): a capability URL is a
 * credential and a customer's published page is the customer's traffic, so
 * neither may beacon to MaiTeams' PostHog project.
 *
 * Two gates, because one alone leaks:
 *   - `startPostHog` decides at page load. Off-surface the SDK is not even
 *     downloaded — the module is imported only when the page qualifies — so a
 *     customer's landing page carries no vendor chunk and fetches nothing.
 *   - `guardEvent` runs as `before_send` for the life of the page, so a
 *     client-side navigation from the app INTO `/share/<token>` or a
 *     `/login?token=…` bounce still sends nothing: the event's own
 *     `$current_url` is re-checked, the event dropped, and the replay
 *     recorder PAUSED until a page view lands on-surface again, so the DOM of
 *     a token page is never recorded (the demo flow pushes `/demo/s/<token>`
 *     client-side). A replay snapshot carries no `$current_url` at all, so it
 *     is judged on the page the browser is on when the batch is sent. Every
 *     other path or URL an event carries, at any depth — the previous page's
 *     pathname, an autocaptured link's `href`, a web-vital's nested
 *     `$current_url`, a heatmap's URL keys, a URL inside an exception
 *     message — goes through the same predicate: deleted when it refuses,
 *     reduced to origin + path when it passes, and a token-shaped path segment
 *     (`/api/demo/s/<token>`) is redacted either way.
 *
 * What is captured (owner decision 2026-09-18, replacing the page-views-only
 * first ship): page views and leaves, explicit `capture` calls, element
 * autocapture (clicks, changes, submits), dead clicks, rage clicks, heatmaps,
 * web vitals, exceptions (unhandled errors, rejections and `console.error`),
 * and session replay with console logs and network TIMINGS — except on Sage's
 * pages and during a live voice call, where replay is off and events are not
 * (`isNoReplayPath`, `holdReplay`; owner, 2026-09-23). The masking
 * policy (owner, 2026-09-28): every input value is masked (passwords always)
 * and so is every piece of page text, because the app shows contacts' names,
 * emails and phone numbers everywhere; the recording keeps the layout, the
 * clicks and the scrolling. The attributes that carry displayed text (`title`,
 * `alt`, `placeholder`, `aria-label`, …) and contact links (`mailto:`/`tel:`)
 * are masked with it, and an attribute value that IS the content is redacted
 * by scheme (`data:`/`blob:`) or by attribute name (`srcdoc`, where a preview
 * iframe's opaque origin leaves its customer email template or landing page
 * in the attribute alone). An element carrying `ph-no-capture` is blocked
 * from replay and autocapture alike. Images are NOT masked. Request and
 * response headers/bodies are never recorded, whatever the project setting
 * says — the SDK's built-in redaction covers `password`/`token`-shaped keys,
 * not a contact's name or email in a CRM response.
 *
 * Feature flags stay off: the flags request carries the initial referrer and
 * person properties with their query strings. Replay needs the project's
 * remote config, which the SDK skips along with flags, so an ALLOWLISTED copy
 * is preloaded from the config endpoint (a GET carrying the token and nothing
 * else): the replay, performance and heatmap settings, never the switches for
 * surveys, product tours, the conversations widget or the Logs product, which
 * would otherwise start from a project toggle outside `before_send`.
 *
 * URLs leave the browser as PATHS: the query can carry a CRM filter value
 * (`?search=<contact name>`) and the fragment a Supabase session
 * (`/onboard#access_token=…`), so every URL-valued property is stripped of
 * both before it is sent, and so is every network-timing entry's URL. Events
 * go straight to the PostHog ingest host — a same-origin proxy would make
 * every beacon carry the app's auth cookies.
 *
 * Pure. Safe in the browser bundle and in jest.
 */
import type { CaptureResult, CapturedNetworkRequest, PostHogConfig } from 'posthog-js'
import { isCapabilityPath, isCredentialBearingUrl, isFirstPartyTrackingSurface } from './first-party-tracking-scope'
import { redactPathTokens } from '@/lib/utils/redact-path-tokens'

const APP_ROOT_DOMAIN = (process.env.NEXT_PUBLIC_APP_ROOT_DOMAIN || 'maiteams.com').toLowerCase()
const LEAD_FORM_DOMAIN = (process.env.NEXT_PUBLIC_LEAD_FORM_DOMAIN || 'leads.yourdomain.com').toLowerCase()
/** The ingest host of the project's cloud. US by default; the EU cloud is `https://eu.i.posthog.com`. */
const POSTHOG_HOST = (process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com').replace(/\/+$/, '')

export interface PageLocation {
  hostname: string
  pathname: string
  search?: string | null
}

/** True where MaiTeams' own product analytics may run on this page. */
export function isPostHogSurface(location: PageLocation): boolean {
  return isFirstPartyTrackingSurface({
    hostname: location.hostname,
    pathname: location.pathname,
    search: location.search,
    appRootDomain: APP_ROOT_DOMAIN,
    leadFormDomain: LEAD_FORM_DOMAIN,
  })
}

/** The page the browser is on right now, or null outside a browser. */
function currentPageLocation(): PageLocation | null {
  if (typeof location === 'undefined' || !location?.hostname) return null
  return { hostname: location.hostname, pathname: location.pathname, search: location.search }
}

/** A URL reduced to origin + path: no query, no fragment. A non-URL comes back unchanged. */
export function stripUrlDetail(value: string): string {
  try {
    const url = new URL(value)
    url.search = ''
    url.hash = ''
    return url.toString()
  } catch {
    return value
  }
}

/** A URL with a scheme and an authority: `https://`, `ws://`; `//`-less forms are paths. */
const SCHEME_URL = /^[a-z][a-z0-9+.-]*:\/\//i
/**
 * A scheme-less authority — `linkedin.com/in/jane-doe` — is a URL a browser
 * resolves relatively and posthog-js captures raw out of an `href`, so it must
 * be judged like any other. The anchor, the required dotted TLD and the
 * trailing `/`, `?` or `#` are what keep ordinary prose out (`'Note: call back
 * Monday'`, `'v1.2.3'`, `'He said "hi"'`). A BARE host with nothing after it is
 * deliberately NOT matched: that is the shape of `$referring_domain` and
 * `$host`, which carry no path and are kept as they arrive.
 */
const SCHEMELESS_HOST = /^(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}(?::\d+)?[/?#]/i
/** A contact URI carries the contact's value itself (`mailto:jane@…`, `tel:+1…`). */
const CONTACT_URI = /^(mailto|tel|sms|callto|facetime):/i
/** An opaque URI is not a page: a `data:` URI is the inline content itself, and `blob:`/`javascript:`/`file:` are meaningless or unsafe off-page. */
const OPAQUE_URI = /^(data|blob|javascript|vbscript|file|filesystem|about):/i
/** Attributes whose value is the content itself, whatever scheme or syntax the value uses. */
const CONTENT_ATTRIBUTES = new Set(['srcdoc'])
/**
 * Attributes whose value is text the page shows or reads out: a tooltip, alt
 * text, a placeholder, an accessible name. Across this codebase they are built
 * from the record on the row (`aria-label={`Remove ${r.email}`}`), so replay
 * masks them like the page text itself.
 */
const TEXT_ATTRIBUTES = new Set([
  'title',
  'alt',
  'placeholder',
  'label',
  'value',
  'aria-label',
  'aria-description',
  'aria-placeholder',
  'aria-roledescription',
  'aria-valuetext',
])
/**
 * A 43-character base64url path segment is a capability token
 * (`lib/utils/redact-path-tokens.ts` explains the shape). Record UUIDs stay:
 * a campaign's id in a page path is what ties a replay to the record.
 */
const TOKEN_SEGMENT = /(^|\/)[A-Za-z0-9_-]{43}(?=\/|$)/g
/**
 * The element attributes autocapture may carry off a page. Any OTHER attribute
 * may hold record content — a `title` tooltip built from a contact field, a
 * `value`, a `placeholder`, a `data-*` note — so its value is emptied whatever
 * it looks like. `aria-label` and `alt` are NOT here for that same reason:
 * across this codebase both are built by interpolation from the record on the
 * row (`aria-label={`Remove ${r.email}`}`), so their values are emptied like
 * any other. `href`/`src`/`action` stay, and their values still go through
 * `scrubString`. posthog-js keys these as `attr__<raw attribute name>`, so a
 * dash spelling and an underscore spelling are the same attribute here.
 */
const ELEMENT_ATTRIBUTE_ALLOWLIST = new Set([
  'class',
  'id',
  'type',
  'role',
  'name',
  'href',
  'src',
  'action',
  'data-testid',
])

/** `attr__data_note` / `attr__data-note` / a bare `href` → the attribute name the allowlist is keyed on. */
function elementAttributeName(key: string): string {
  return (key.startsWith('attr__') ? key.slice('attr__'.length) : key).replace(/_/g, '-').toLowerCase()
}
/** Nested bags are walked this deep; PostHog's own payloads are at most three levels. Deeper is deleted, never kept. */
const MAX_BAG_DEPTH = 8

/**
 * Judge one string. A bare path is judged on the event's own origin, a URL on
 * its own host, and only http(s) can pass; a contact or opaque URI never
 * does. Refused → null. Passed → the URL reduced to origin + path, or the
 * path reduced to its pathname, with any token-shaped segment redacted. A
 * scheme-less authority (`SCHEMELESS_HOST`) is judged on `https://` and comes
 * back scheme-less. A string that starts with `/`, or that reads as a
 * scheme-less authority, but cannot be classified is refused, never kept; any
 * other string is not a URL and comes back unchanged.
 */
function scrubString(value: string, pageOrigin: string): string | null {
  if (CONTACT_URI.test(value) || OPAQUE_URI.test(value)) return null
  const isPath = value.startsWith('/')
  let schemeless = false
  if (!isPath && !SCHEME_URL.test(value)) {
    if (!SCHEMELESS_HOST.test(value)) return value
    schemeless = true
  }
  let target: URL
  try {
    target = isPath ? new URL(value, pageOrigin) : new URL(schemeless ? `https://${value}` : value)
  } catch {
    return isPath || schemeless ? null : value
  }
  const onSurface =
    (target.protocol === 'http:' || target.protocol === 'https:') &&
    isPostHogSurface({ hostname: target.hostname, pathname: target.pathname, search: target.search })
  if (!onSurface) return null
  target.search = ''
  target.hash = ''
  target.pathname = target.pathname.replace(TOKEN_SEGMENT, '$1[redacted-token]')
  // Give the value back in the shape it arrived in; a scheme-less link is not rewritten into an absolute one.
  if (isPath) return target.pathname
  return schemeless ? target.toString().replace(/^https:\/\//, '') : target.toString()
}

/**
 * Walk a property bag in place, at any depth: every path- or URL-valued
 * string is judged by `scrubString`; a refused object value is deleted and a
 * refused array entry is blanked (indices are positions in an elements
 * chain). Past the depth cap the bag is emptied: fail closed.
 */
function scrubBag(value: unknown, pageOrigin: string, depth = 0): void {
  if (!value || typeof value !== 'object') return
  if (Array.isArray(value)) {
    if (depth > MAX_BAG_DEPTH) {
      value.length = 0
      return
    }
    for (let i = 0; i < value.length; i++) {
      const item = value[i]
      if (typeof item === 'string') value[i] = scrubString(item, pageOrigin) ?? ''
      else scrubBag(item, pageOrigin, depth + 1)
    }
    return
  }
  const record = value as Record<string, unknown>
  for (const key of Object.keys(record)) {
    if (depth > MAX_BAG_DEPTH) {
      delete record[key]
      continue
    }
    const item = record[key]
    if (typeof item === 'string') {
      const kept = scrubString(item, pageOrigin)
      if (kept === null) delete record[key]
      else if (kept !== item) record[key] = kept
    } else if (item && typeof item === 'object') {
      scrubBag(item, pageOrigin, depth + 1)
    }
  }
}

/**
 * Autocapture also serialises the clicked element's ancestry as one string,
 * `$elements_chain`, with every element attribute inline as `attr__<name>="…"`
 * (plus a bare `href="…"`). An attribute outside
 * `ELEMENT_ATTRIBUTE_ALLOWLIST` is emptied outright; an allowlisted value is
 * judged like any other string, and a refused one is emptied too.
 */
function scrubElementsChain(chain: string, pageOrigin: string): string {
  return chain.replace(
    /((attr__[\w-]+|href)=")((?:[^"\\]|\\.)*)(")/g,
    (_match, open: string, key: string, value: string, close: string) => {
      const kept = ELEMENT_ATTRIBUTE_ALLOWLIST.has(elementAttributeName(key)) ? scrubString(value, pageOrigin) : null
      return `${open}${kept ?? ''}${close}`
    }
  )
}

/**
 * The OBJECT form of that same ancestry: a project with the remote
 * `elementsChainAsString` setting off gets `$elements`, one record per
 * element, with the same `attr__<name>` keys. Every attribute outside
 * `ELEMENT_ATTRIBUTE_ALLOWLIST` is deleted here, before `scrubBag` judges what
 * is left as ordinary strings.
 */
function scrubElementsArray(elements: unknown[]): void {
  for (const element of elements) {
    if (!element || typeof element !== 'object' || Array.isArray(element)) continue
    const record = element as Record<string, unknown>
    for (const key of Object.keys(record)) {
      if (!key.startsWith('attr__')) continue
      if (!ELEMENT_ATTRIBUTE_ALLOWLIST.has(elementAttributeName(key))) delete record[key]
    }
  }
}

/**
 * A URL or path inside prose — an exception message built from
 * `console.error` arguments, say — is judged in place: refused → `[url]`,
 * kept → origin + path.
 */
function scrubProse(text: string, pageOrigin: string): string {
  return text
    .replace(/https?:\/\/[^\s"'<>)\]]+/gi, (url) => scrubString(url, pageOrigin) ?? '[url]')
    .replace(/(^|[\s(])(\/[^\s"'<>)\]]*)/g, (_match, lead: string, path: string) => `${lead}${scrubString(path, pageOrigin) ?? '[path]'}`)
}

/**
 * What replaces a console-sourced exception body. The arguments of a
 * `console.error` call are joined verbatim into the exception's `value`, and
 * across this codebase those arguments carry record content — a contact's
 * name, an org's field values, an API response body. The callsites are too
 * many to audit one by one, so the body is dropped wholesale here; the
 * stacktrace survives, which is what identifies the callsite.
 */
const CONSOLE_EXCEPTION_REDACTED = '[console.error arguments redacted]'
/** posthog-js tags a `console.error` capture with this mechanism (`generic`/`onerror`/`onunhandledrejection` are real throws). */
const CONSOLE_EXCEPTION_MECHANISM = 'onconsole'

/** rrweb's console plugin, which carries the SAME arguments down the REPLAY's path. */
const CONSOLE_REPLAY_PLUGIN = 'rrweb/console@1'
/** What replaces them there, for the reason stated above `CONSOLE_EXCEPTION_REDACTED`. */
const CONSOLE_REPLAY_REDACTED = '[console arguments redacted]'

/** The exception fields a `console.error` argument lands in verbatim. */
function scrubException(properties: Record<string, unknown>, pageOrigin: string): void {
  const list = properties.$exception_list
  if (Array.isArray(list)) {
    let sawConsole = false
    for (const entry of list) {
      if (!entry || typeof entry !== 'object') continue
      const item = entry as { value?: unknown; mechanism?: { type?: unknown } }
      const fromConsole = item.mechanism?.type === CONSOLE_EXCEPTION_MECHANISM
      if (fromConsole) sawConsole = true
      if (typeof item.value !== 'string') continue
      item.value = fromConsole ? CONSOLE_EXCEPTION_REDACTED : scrubProse(item.value, pageOrigin)
    }
    if (sawConsole) {
      // `$exception_message` mirrors the first entry's value; never create it where the SDK did not.
      if (typeof properties.$exception_message === 'string') properties.$exception_message = CONSOLE_EXCEPTION_REDACTED
      return
    }
  }
  if (typeof properties.$exception_message === 'string') {
    properties.$exception_message = scrubProse(properties.$exception_message, pageOrigin)
  }
}

/**
 * The replay's copy of that same leak. rrweb's console plugin records every
 * `console.*` call as a type-6 plugin event whose `data.payload.payload` is the
 * call's arguments, stringified — the body `scrubException` drops on the event
 * path, arriving by another road. posthog-js compresses only type 2 and type 3
 * (source 0 or 8), so a plugin event reaches `before_send` as plain JSON and is
 * redacted in place. The level and the stacktrace survive, which is what
 * identifies the callsite. Entries of every other type — the DOM recording
 * itself — are left exactly as they came.
 */
function scrubSnapshotConsole(properties: Record<string, unknown>): void {
  const data = properties.$snapshot_data
  // Batched as an array; the immediate-flush path hands over a single event.
  const entries = Array.isArray(data) ? data : [data]
  for (const entry of entries) {
    if (!entry || typeof entry !== 'object') continue
    const item = entry as { type?: unknown; data?: { plugin?: unknown; payload?: { payload?: unknown } } }
    if (item.type !== 6 || item.data?.plugin !== CONSOLE_REPLAY_PLUGIN) continue
    const payload = item.data.payload
    if (payload && Array.isArray(payload.payload)) payload.payload = [CONSOLE_REPLAY_REDACTED]
  }
  // `$snapshot_bytes` is left alone: it is an ingest size hint, now a slight
  // overestimate, and not a value anything reads back.
}

/**
 * A heatmap event keys its samples by the page URL they were taken on. Keys
 * are judged like any URL: refused pages are dropped, kept ones are reduced to
 * origin + path, and samples of pages that collapse to one key are merged.
 */
function scrubHeatmapData(properties: Record<string, unknown>, pageOrigin: string): void {
  const data = properties.$heatmap_data
  if (!data || typeof data !== 'object' || Array.isArray(data)) return
  const merged: Record<string, unknown[]> = {}
  for (const [url, samples] of Object.entries(data as Record<string, unknown>)) {
    const kept = scrubString(url, pageOrigin)
    if (kept === null) continue
    const list = Array.isArray(samples) ? samples : []
    merged[kept] = (merged[kept] ?? []).concat(list)
  }
  properties.$heatmap_data = merged
}

/**
 * Pages whose events are kept but whose DOM is never replayed. Sage holds a
 * voice call for up to half an hour on a phone, and on iPhones its page was
 * killed mid-call nine times in the 30 hours after continuous replay went live
 * (2026-09-21), never on Android or desktop. Replay stays off there until that
 * is ruled out (owner, 2026-09-23). A live voice session holds replay off
 * wherever it runs — the Sage dock too — through `holdReplay`.
 */
const NO_REPLAY_PATH_PREFIXES = ['/sage'] as const

/** True for a page whose DOM is never replayed (its events still are sent). */
export function isNoReplayPath(pathname: string): boolean {
  const path = pathname.toLowerCase()
  return NO_REPLAY_PATH_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`))
}

let started: PostHogLike | null = null
let recordingPaused = false
/** The last page judged may be replayed: on-surface and not a no-replay page. */
let pageReplayable = true
let replayHolds = 0

/** Stop the recorder the moment a page goes off-surface, onto a no-replay page, or a hold is taken, so that DOM is never captured; its final flush is dropped by the snapshot judgement. */
function pauseRecording(): void {
  if (!started || recordingPaused) return
  recordingPaused = true
  try {
    started.stopSessionRecording()
  } catch {
    // The recorder may not have loaded yet; there is nothing to stop.
  }
}

/** Resume, with a fresh snapshot, once a replayable page view lands and no hold is left. */
function resumeRecording(): void {
  if (!started || !recordingPaused || !pageReplayable || replayHolds > 0) return
  recordingPaused = false
  try {
    started.startSessionRecording()
  } catch {
    // Same as above.
  }
}

/**
 * Keep the replay recorder off until the returned release is called — a live
 * voice session holds it for as long as the call runs. Events are unaffected.
 * Holds nest; the recorder resumes only when the last one is released and the
 * page itself may be replayed.
 */
export function holdReplay(): () => void {
  replayHolds++
  pauseRecording()
  let released = false
  return () => {
    if (released) return
    released = true
    replayHolds--
    resumeRecording()
  }
}

/**
 * `before_send`: drop the event unless its page is a PostHog surface, delete
 * every path or URL it carries that points off-surface, then reduce the URLs
 * left to a path. Fail closed — no `$current_url`, or one that does not parse,
 * is a drop. The one event without a `$current_url` by design is the replay
 * `$snapshot` batch: it is judged on the page the browser is on when it is
 * sent (`currentLocation`, injectable for tests), and the only thing read
 * inside a kept one is its console plugin entries (see `scrubSnapshotConsole`).
 * An off-surface page also pauses the recorder; an on-surface page view
 * resumes it.
 */
export function guardEvent(
  event: CaptureResult | null,
  currentLocation: () => PageLocation | null = currentPageLocation
): CaptureResult | null {
  if (!event) return null
  if (event.event === '$snapshot') {
    const here = currentLocation()
    if (here && isPostHogSurface(here) && !isNoReplayPath(here.pathname) && replayHolds === 0) {
      if (event.properties) scrubSnapshotConsole(event.properties as Record<string, unknown>)
      return event
    }
    pauseRecording()
    return null
  }
  const url = event.properties?.$current_url
  if (typeof url !== 'string') return null
  let parsed: URL
  try {
    parsed = new URL(url)
  } catch {
    return null
  }
  if (!isPostHogSurface({ hostname: parsed.hostname, pathname: parsed.pathname, search: parsed.search })) {
    pageReplayable = false
    pauseRecording()
    return null
  }
  if (event.event === '$pageview') {
    pageReplayable = !isNoReplayPath(parsed.pathname)
    if (pageReplayable) resumeRecording()
    else pauseRecording()
  }
  const properties = event.properties as Record<string, unknown> | undefined
  if (properties) {
    if (typeof properties.$elements_chain === 'string') {
      properties.$elements_chain = scrubElementsChain(properties.$elements_chain, parsed.origin)
    }
    if (Array.isArray(properties.$elements)) scrubElementsArray(properties.$elements)
    if (event.event === '$$heatmap') scrubHeatmapData(properties, parsed.origin)
    if (event.event === '$exception') scrubException(properties, parsed.origin)
  }
  scrubBag(event.properties, parsed.origin)
  scrubBag(event.$set, parsed.origin)
  scrubBag(event.$set_once, parsed.origin)
  return event
}

/**
 * `session_recording.maskCapturedNetworkRequestFn`: replay keeps network
 * TIMINGS only. Headers and bodies are deleted whatever a remote setting
 * says. A request URL is judged like any other — a capability or credential
 * URL drops the entry — and every other URL (the app's own API, Supabase, a
 * vendor) is reduced to origin + path with UUID and token segments redacted,
 * because a query string is where a filter value or a filter's contact name
 * travels and an API path is where a capability token does. A timing entry
 * that is not a URL at all (`first-contentful-paint`) is kept as it is.
 */
export function maskNetworkRequest(request: CapturedNetworkRequest): CapturedNetworkRequest | null {
  delete request.requestHeaders
  delete request.responseHeaders
  delete request.requestBody
  delete request.responseBody
  if (typeof request.name !== 'string' || !SCHEME_URL.test(request.name)) return request
  let target: URL
  try {
    target = new URL(request.name)
  } catch {
    return null
  }
  if (isCapabilityPath(target.pathname) || isCredentialBearingUrl(target.pathname, target.search)) return null
  target.search = ''
  target.hash = ''
  target.pathname = redactPathTokens(target.pathname)
  request.name = target.toString()
  return request
}

/**
 * `session_recording.maskAttributeFn`: an attribute value that IS the content
 * never enters a DOM snapshot. `OPAQUE_URI` already deletes a `data:`/`blob:`
 * value from an EVENT's property bag; replay is the other road for the same
 * bytes, and the asymmetry is what makes it worth closing — a hosted image
 * travels as a URL PostHog can never resolve, while a `data:` URI carries the
 * pixels themselves (a scanned document pasted into the realtime copilot, a
 * generated image in AI content, an uploaded photo). `srcdoc` is redacted by
 * attribute name: its value is the customer's whole email template or landing
 * page, and a preview iframe's opaque origin makes the attribute the recorder's
 * only road to that document. A text-bearing attribute (`TEXT_ATTRIBUTES`) is
 * masked the way rrweb masks page text, one `*` per visible character, and a
 * contact link (`mailto:`/`tel:`/`sms:`) is redacted, because its value is the
 * contact's email or number. The value is replaced, not the element: classes,
 * styles, ids, `data-*` state and ordinary links arrive as they are, so the
 * replay keeps its layout. A URI embedded in a longer value
 * (`style="background:url(data:…)"`) is not matched, the same limit the event
 * path has.
 */
export function maskReplayAttribute(name: string, value: string): string {
  const attribute = name.toLowerCase()
  if (CONTENT_ATTRIBUTES.has(attribute)) return '[redacted-inline-content]'
  if (TEXT_ATTRIBUTES.has(attribute)) return value.replace(/\S/g, '*')
  if (CONTACT_URI.test(value.trim())) return '[redacted-contact]'
  return OPAQUE_URI.test(value) ? '[redacted-inline-content]' : value
}

/** The PostHog app for the toolbar: `https://us.i.posthog.com` → `https://us.posthog.com`. */
export function posthogUiHost(ingestHost: string = POSTHOG_HOST): string {
  return ingestHost.replace('.i.posthog.com', '.posthog.com')
}

/**
 * `replay: false` starts the SDK with the recorder off — a page that loads
 * straight onto a no-replay page never downloads or runs it; the first page
 * view that may be replayed starts it.
 */
export function buildPostHogConfig({ replay = true }: { replay?: boolean } = {}): Partial<PostHogConfig> {
  return {
    api_host: POSTHOG_HOST,
    ui_host: posthogUiHost(),
    // A pin, not "latest": a later snapshot changes what is captured by
    // default and is adopted only after review.
    defaults: '2026-05-30',
    // Element-level capture. Rage clicks keep the snapshot's default (on, with
    // its pagination/stepper ignore lists), so they are not set here.
    autocapture: true,
    capture_dead_clicks: true,
    capture_heatmaps: true,
    capture_performance: { network_timing: true, web_vitals: true },
    capture_exceptions: {
      capture_unhandled_errors: true,
      capture_unhandled_rejections: true,
      capture_console_errors: true,
    },
    // Session replay, with the browser console in the recording. Set here so a
    // project toggle cannot silently change what leaves the browser.
    disable_session_recording: !replay,
    enable_recording_console_log: true,
    session_recording: {
      // Stated here so it wins over the project's "Privacy and masking"
      // setting: every input value masked, passwords always.
      // `ph-no-capture` blocks an element outright.
      maskAllInputs: true,
      // Every text node masked (owner, 2026-09-28): PostHog's documented
      // "mask all text" selector. rrweb replaces each visible character with
      // `*`, so lengths and layout survive while names, emails, phone numbers
      // and message drafts (a contenteditable is ordinary text nodes) do not.
      // Style and script text are not page text and are left alone.
      maskTextSelector: '*',
      recordHeaders: false,
      recordBody: false,
      recordCrossOriginIframes: false,
      // The client value wins over the project's canvas toggle: the recorder
      // resolves `captureCanvas?.recordCanvas ?? canvasRecording.enabled`, and
      // `false` is non-nullish. Without it a dashboard toggle would put a
      // canvas's pixels — VideoRecorder paints live webcam frames into one —
      // into the replay.
      captureCanvas: { recordCanvas: false },
      maskAttributeFn: maskReplayAttribute,
      maskCapturedNetworkRequestFn: maskNetworkRequest,
    },
    // Belt to guardEvent's braces: the SDK never records the fragment either.
    disable_capture_url_hashes: true,
    // The flags request is sent outside before_send, and its person_properties
    // carry $initial_current_url / $initial_referrer with their full query
    // strings (`/signup?token=…&email=…`). Nothing here reads a flag. The
    // remote config it would have carried is preloaded instead (see
    // `preloadRemoteConfig`), allowlisted.
    advanced_disable_flags: true,
    // Surfaces a project toggle could otherwise switch on outside before_send:
    // the Logs product (console output to its own endpoint; the REPLAY's
    // console recording is a different path and is on), surveys (with flags
    // off an untargeted survey would show to everyone), product tours and the
    // conversations widget (rendered UI). All off until designed and tested.
    logs: { captureConsoleLogs: false },
    disable_surveys: true,
    disable_product_tours: true,
    disable_conversations: true,
    before_send: guardEvent,
  }
}

export interface PostHogLike {
  init: (token: string, config: Partial<PostHogConfig>) => unknown
  identify: (distinctId: string, properties?: Record<string, unknown>) => void
  get_property: (name: string) => unknown
  reset: () => void
  /** Super properties: sent on every later event from this browser until `reset`. */
  register: (properties: Record<string, unknown>) => void
  /** Drops a super property; `PostHogOrganization` clears attribution the placeholder withholds. */
  unregister: (property: string) => void
  /** Group analytics: ties this browser's later events to `groupKey` of `groupType`. */
  group: (groupType: string, groupKey: string, properties?: Record<string, unknown>) => void
  /** Drops every registered group (`$groups`), the group half of the same clear. */
  resetGroups: () => void
  /** The groups registered on this browser, by type. */
  getGroups: () => Record<string, unknown>
  /** The PostHog URL of the replay of the current session, at the current moment when asked with a timestamp. */
  get_session_replay_url: (options?: { withTimestamp?: boolean; timestampLookBack?: number }) => string
  sessionRecordingStarted: () => boolean
  startSessionRecording: () => void
  stopSessionRecording: () => void
}

/** Loads the SDK module on demand — `() => import('posthog-js')` in the app. */
export type PostHogLoader = () => Promise<{ default: PostHogLike }>

let instance: Promise<PostHogLike | null> = Promise.resolve(null)

/** The global posthog-js reads a preloaded remote config from; its own `config.js` loader fills the same one. */
type RemoteConfigHolder = { _POSTHOG_REMOTE_CONFIG?: Record<string, { config: unknown; siteApps?: unknown[] }> }

/**
 * The remote-config keys the SDK may see. Replay, performance and heatmap
 * settings, the ingest details, error-tracking suppression rules, and the
 * project's autocapture opt-out. Everything else — surveys, product tours,
 * conversations, the Logs product, site apps, flags — starts a request or a
 * UI from a project toggle, and stays out until it has a request-body test.
 */
const REMOTE_CONFIG_ALLOWLIST = [
  'sessionRecording',
  'capturePerformance',
  'heatmaps',
  'elementsChainAsString',
  'supportedCompression',
  'analytics',
  'errorTracking',
  'defaultIdentifiedOnly',
  'autocapture_opt_out',
] as const

/**
 * The project's remote config — replay's endpoint, recorder version,
 * console-log flag, sampling and triggers — normally rides on the
 * feature-flags request, which stays disabled (its body carries the initial
 * referrer and person properties). Without it the SDK never starts replay.
 * The SDK also honours a copy preloaded on `window._POSTHOG_REMOTE_CONFIG`,
 * so an allowlisted copy is fetched here from the config endpoint: a GET that
 * carries the project token and nothing else, cross-origin, no cookies. A
 * failed fetch leaves everything but replay running on the local config.
 */
export async function preloadRemoteConfig(token: string, holder: RemoteConfigHolder = globalThis as RemoteConfigHolder): Promise<boolean> {
  try {
    const response = await fetch(`${POSTHOG_HOST}/array/${encodeURIComponent(token)}/config`, {
      credentials: 'omit',
      cache: 'no-store',
    })
    if (!response.ok) return false
    const remote: unknown = await response.json()
    if (!remote || typeof remote !== 'object' || Array.isArray(remote)) return false
    const config: Record<string, unknown> = { hasFeatureFlags: false }
    for (const key of REMOTE_CONFIG_ALLOWLIST) {
      if (key in remote) config[key] = (remote as Record<string, unknown>)[key]
    }
    // Autocapture reads its own state off the remote config: having SEEN one,
    // the SDK treats an absent `autocapture_opt_out` as "not enabled" and stops
    // capturing (posthog-js 1.431.2, Autocapture#isEnabled reads the remote
    // value first and the config's `autocapture` second). A project that has
    // not opted out omits the key, so the preloaded copy states the default
    // and a real project opt-out still arrives as `true` and is honoured.
    if (!('autocapture_opt_out' in config)) config.autocapture_opt_out = false
    holder._POSTHOG_REMOTE_CONFIG = { ...(holder._POSTHOG_REMOTE_CONFIG ?? {}), [token]: { config, siteApps: [] } }
    return true
  } catch {
    return false
  }
}

/**
 * Start the SDK when there is a project token AND the page is a surface
 * MaiTeams may track AND the visitor's cookie choice allows analytics
 * (`allowed`, from `lib/privacy/consent-runtime.ts`); only then is the module
 * downloaded (and the remote config fetched, in parallel). Unset token = not
 * configured: nothing loads (the same rule as Dub and New Relic). `allowed` is
 * asked only once the first two hold, and while it has not resolved nothing
 * is fetched and `posthogClient()` stays pending, so a visitor who accepts
 * later in the page starts it then. Resolves to the started client, or null
 * — a refusal or a chunk that fails to load is null too.
 */
export function startPostHog(
  load: PostHogLoader,
  token: string | undefined,
  location: PageLocation,
  currentLocation: () => PageLocation | null = currentPageLocation,
  allowed: () => Promise<boolean> = () => Promise.resolve(true),
  allowedNow?: () => boolean
): Promise<PostHogLike | null> {
  started = null
  recordingPaused = false
  pageReplayable = !isNoReplayPath(location.pathname)
  if (!token || !isPostHogSurface(location)) {
    instance = Promise.resolve(null)
    return instance
  }
  instance = allowed()
    .then((ok) => {
      const here = currentLocation()
      if (!ok || !here || !isPostHogSurface(here) || (allowedNow && !allowedNow())) return null
      return Promise.all([load(), preloadRemoteConfig(token)])
    })
    .then((loaded) => {
      if (!loaded || (allowedNow && !allowedNow())) return null
      const [{ default: posthog }, preloaded] = loaded
      // The chunk and the remote config resolve asynchronously; a client-side
      // navigation in that window can have moved the browser onto a no-replay
      // page, and there was no SDK yet to emit that page view. Narrowing only:
      // a page that was judged unreplayable at load stays unreplayable here.
      const here = currentLocation()
      if (!here || !isPostHogSurface(here)) return null
      if (isNoReplayPath(here.pathname)) pageReplayable = false
      // A hold can be taken while the chunk loads; the recorder starts off then too.
      const replay = pageReplayable && replayHolds === 0
      posthog.init(token, {
        ...buildPostHogConfig({ replay }),
        before_send: allowedNow ? (event) => {
          if (!allowedNow()) {
            pauseRecording()
            return null
          }
          return guardEvent(event)
        } : guardEvent,
      })
      started = posthog
      recordingPaused = !replay
      if (!preloaded) console.warn('[posthog] remote config unavailable: session replay is off for this page load')
      return posthog
    })
    .catch(() => null)
  return instance
}

/** The client started for this page, or null wherever it never started. */
export function posthogClient(): Promise<PostHogLike | null> {
  return instance
}

/**
 * `reset()` drops the super properties and the `$groups` along with the
 * identity, and supabase-js emits a SPURIOUS `SIGNED_OUT` during a multi-tab
 * refresh-token race — on the shells that do not remount on it, a tab loses
 * its org attribution for the rest of the page's life while the session
 * recovers a beat later. IDENTITY is the signal that it recovered:
 * `PostHogIdentity` announces every `identify()` here and
 * `PostHogOrganization` re-asserts the active org on it. A real sign-out
 * identifies nobody, so nothing is re-registered behind it.
 */
const identifiedListeners = new Set<() => void>()

/** Run `listener` on every later `identify()`; returns the unsubscribe. */
export function onPostHogIdentified(listener: () => void): () => void {
  identifiedListeners.add(listener)
  return () => {
    identifiedListeners.delete(listener)
  }
}

/** Announce that this browser has been identified — `PostHogIdentity` is the only caller. */
export function notifyPostHogIdentified(): void {
  for (const listener of [...identifiedListeners]) listener()
}


/** The started client right now, for a caller that cannot await (Sentry's `beforeSend`); null until it has started. */
export function posthogClientSync(): PostHogLike | null {
  return started
}
